import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, Upload, Building, CreditCard, Bell, Lock, ChevronRight, CheckCircle2, Edit2, X, Clock, ShieldCheck, AlertCircle, Save } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
type ApprovalStatus = 'approved' | 'pending' | 'rejected' | 'none';
interface FieldState {
  value: string;
  originalValue: string;
  approvalStatus: ApprovalStatus;
  isEditing: boolean;
}
export function VendorStoreSettingsScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    store
  } = useVendor();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [fields, setFields] = useState<Record<string, FieldState>>({
    storeName: {
      value: store.name,
      originalValue: store.name,
      approvalStatus: 'approved',
      isEditing: false
    },
    description: {
      value: store.description,
      originalValue: store.description,
      approvalStatus: 'approved',
      isEditing: false
    },
    businessName: {
      value: store.businessInfo.businessName,
      originalValue: store.businessInfo.businessName,
      approvalStatus: 'approved',
      isEditing: false
    },
    businessAddress: {
      value: store.businessInfo.businessAddress,
      originalValue: store.businessInfo.businessAddress,
      approvalStatus: 'approved',
      isEditing: false
    },
    taxId: {
      value: store.businessInfo.taxId || '',
      originalValue: store.businessInfo.taxId || '',
      approvalStatus: 'approved',
      isEditing: false
    },
    accountName: {
      value: store.bankDetails?.accountName || '',
      originalValue: store.bankDetails?.accountName || '',
      approvalStatus: 'approved',
      isEditing: false
    },
    accountNumber: {
      value: store.bankDetails?.accountNumber || '',
      originalValue: store.bankDetails?.accountNumber || '',
      approvalStatus: 'approved',
      isEditing: false
    },
    bankName: {
      value: store.bankDetails?.bankName || '',
      originalValue: store.bankDetails?.bankName || '',
      approvalStatus: 'approved',
      isEditing: false
    }
  });
  const toggleEdit = (key: string) => {
    setFields(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        isEditing: !prev[key].isEditing
      }
    }));
  };
  const updateField = (key: string, value: string) => {
    setFields(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value
      }
    }));
  };
  const cancelEdit = (key: string) => {
    setFields(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: prev[key].originalValue,
        isEditing: false
      }
    }));
  };
  const hasChanges = (keys: string[]) => keys.some(k => fields[k].value !== fields[k].originalValue);
  const handleSubmitSection = (sectionKeys: string[], sectionName: string) => {
    const changed = sectionKeys.filter(k => fields[k].value !== fields[k].originalValue);
    if (changed.length === 0) return;
    const updated: Record<string, FieldState> = {
      ...fields
    };
    changed.forEach(k => {
      updated[k] = {
        ...updated[k],
        approvalStatus: 'pending',
        isEditing: false,
        originalValue: updated[k].value
      };
    });
    sectionKeys.forEach(k => {
      updated[k] = {
        ...updated[k],
        isEditing: false
      };
    });
    setFields(updated);
    setSuccessMessage(`${sectionName} changes submitted for admin approval`);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };
  const getStatusBadge = (status: ApprovalStatus) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" data-id="element-2464">
            <ShieldCheck className="w-3 h-3" data-id="element-2465" /> Approved
          </span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" data-id="element-2466">
            <Clock className="w-3 h-3" data-id="element-2467" /> Pending Approval
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" data-id="element-2468">
            <AlertCircle className="w-3 h-3" data-id="element-2469" /> Rejected
          </span>;
      default:
        return null;
    }
  };
  const renderField = (key: string, label: string, isTextarea?: boolean) => {
    const field = fields[key];
    return <div key={key} data-id="element-2470">
        <div className="flex items-center justify-between mb-1" data-id="element-2471">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-2472">
            {label}
          </label>
          <div className="flex items-center gap-2" data-id="element-2473">
            {field.approvalStatus !== 'none' && getStatusBadge(field.approvalStatus)}
            {!field.isEditing && field.approvalStatus !== 'pending' && <button onClick={() => toggleEdit(key)} className="p-1 text-primary hover:bg-primary/10 rounded-lg transition-colors" data-id="element-2474">
                <Edit2 className="w-3.5 h-3.5" data-id="element-2475" />
              </button>}
          </div>
        </div>
        <AnimatePresence mode="wait" data-id="element-2476">
          {field.isEditing ? <motion.div key="editing" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} data-id="element-2477">
              {isTextarea ? <textarea value={field.value} onChange={e => updateField(key, e.target.value)} rows={3} className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-primary/30 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary dark:text-white resize-none" data-id="element-2478" /> : <input type="text" value={field.value} onChange={e => updateField(key, e.target.value)} className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-primary/30 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary dark:text-white" data-id="element-2479" />}
              <button onClick={() => cancelEdit(key)} className="mt-1 text-xs text-gray-500 hover:text-red-500 flex items-center gap-1" data-id="element-2480">
                <X className="w-3 h-3" data-id="element-2481" /> Cancel
              </button>
            </motion.div> : <motion.p key="display" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className={`text-sm ${field.approvalStatus === 'pending' ? 'text-yellow-700 dark:text-yellow-400 italic' : 'text-gray-900 dark:text-white'}`} data-id="element-2482">
              {field.value || <span className="text-gray-400" data-id="element-2483">Not set</span>}
            </motion.p>}
        </AnimatePresence>
      </div>;
  };
  const sections = [{
    title: 'Store Profile',
    icon: Store,
    keys: ['storeName', 'description'],
    fields: [{
      key: 'storeName',
      label: 'Store Name'
    }, {
      key: 'description',
      label: 'Description',
      isTextarea: true
    }]
  }, {
    title: 'Business Information',
    icon: Building,
    keys: ['businessName', 'businessAddress', 'taxId'],
    fields: [{
      key: 'businessName',
      label: 'Business Name'
    }, {
      key: 'businessAddress',
      label: 'Business Address',
      isTextarea: true
    }, {
      key: 'taxId',
      label: 'Tax ID (TIN)'
    }]
  }, {
    title: 'Bank Details',
    icon: CreditCard,
    keys: ['accountName', 'accountNumber', 'bankName'],
    fields: [{
      key: 'accountName',
      label: 'Account Name'
    }, {
      key: 'accountNumber',
      label: 'Account Number'
    }, {
      key: 'bankName',
      label: 'Bank Name'
    }]
  }];
  const quickSettings = [{
    icon: Bell,
    label: 'Notifications',
    description: 'Manage notification preferences',
    screen: Screen.NOTIFICATIONS
  }, {
    icon: Lock,
    label: 'Password & Security',
    description: 'Change password and security settings',
    screen: Screen.CHANGE_PASSWORD
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24" data-id="element-2484">
      <Header title="Store Settings" showBack={false} data-id="element-2485" />

      {/* Admin Approval Notice */}
      <div className="mx-4 mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-3 rounded-xl flex items-start gap-3" data-id="element-2486">
        <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-2487" />
        <p className="text-xs text-blue-700 dark:text-blue-300" data-id="element-2488">
          Changes to your store settings require admin approval. Edited fields
          will be marked as{' '}
          <span className="font-semibold" data-id="element-2489">Pending Approval</span> until
          reviewed.
        </p>
      </div>

      <div className="p-4 space-y-4" data-id="element-2490">
        {/* Store Logo/Banner */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700" data-id="element-2491">
          <h3 className="font-bold text-gray-900 dark:text-white mb-3" data-id="element-2492">
            Store Branding
          </h3>
          <div className="space-y-3" data-id="element-2493">
            <div data-id="element-2494">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2" data-id="element-2495">
                Store Logo
              </label>
              <button className="w-full aspect-video bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors" data-id="element-2496">
                <Upload className="w-6 h-6 text-gray-400" data-id="element-2497" />
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2498">
                  Upload Logo
                </span>
              </button>
            </div>
            <div data-id="element-2499">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2" data-id="element-2500">
                Store Banner
              </label>
              <button className="w-full aspect-[3/1] bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors" data-id="element-2501">
                <Upload className="w-6 h-6 text-gray-400" data-id="element-2502" />
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2503">
                  Upload Banner
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Editable Settings Sections */}
        {sections.map(section => <div key={section.title} className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700" data-id="element-2504">
            <div className="flex items-center gap-2 mb-4" data-id="element-2505">
              <section.icon className="w-5 h-5 text-primary" data-id="element-2506" />
              <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2507">
                {section.title}
              </h3>
            </div>

            <div className="space-y-4" data-id="element-2508">
              {section.fields.map(f => renderField(f.key, f.label, f.isTextarea))}
            </div>

            {hasChanges(section.keys) && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2509">
                <Button fullWidth onClick={() => handleSubmitSection(section.keys, section.title)} className="bg-primary hover:bg-primary/90 text-white" data-id="element-2510">
                  <Save className="w-4 h-4 mr-2" data-id="element-2511" />
                  Submit for Approval
                </Button>
              </motion.div>}
          </div>)}

        {/* Quick Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700 overflow-hidden" data-id="element-2512">
          {quickSettings.map((setting, idx) => <button key={setting.label} onClick={() => navigate(setting.screen)} className={`w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors ${idx !== quickSettings.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}`} data-id="element-2513">
              <div className="w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-xl flex items-center justify-center" data-id="element-2514">
                <setting.icon className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-2515" />
              </div>
              <div className="flex-1 text-left" data-id="element-2516">
                <p className="font-medium text-gray-900 dark:text-white" data-id="element-2517">
                  {setting.label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2518">
                  {setting.description}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-2519" />
            </button>)}
        </div>
      </div>

      {/* Success Toast */}
      <AnimatePresence data-id="element-2520">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-2521">
            <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-2522">
              <Clock className="w-5 h-5 text-yellow-400" data-id="element-2523" />
              <span className="text-sm font-medium" data-id="element-2524">{successMessage}</span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}